import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import toast from "react-hot-toast";
import { Button, Spinner } from "react-bootstrap";
import { Spin } from "antd";
import * as Sentry from "@sentry/react";

import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useOrder } from "@/_zustand/order";
import { formatMoney, mapCartForOrder, buildAppliedDiscount } from "@/lib";
import { useOneClickComercio } from "@/hooks/useOneClickComercio";
import { MSG_TARJETA_OTRO_COMERCIO } from "@/lib/oneclickCards";
import {
  adquirirCandadoPago,
  liberarCandadoPago,
  esTokenVigente,
  debeReportarBloqueo,
  bloqueosAcumulados,
  reclamarIntento,
  generarBuyOrderOneClick,
  PAYMENT_MSG_EN_CURSO,
} from "@/lib/paymentGuard";
import {
  useAuthorizePayOneClick,
  useReverseOneClickTransaction,
  useCreateOrder,
} from "@/hooks/useTransbank";
import { filtrarCredencialesParaBody } from "@/lib/transbankCredenciales";

export const OneClick = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  const { user, tempCard, setTempCard, incrementTotalPedidos } = useAuth();
  const { comercioEfectivo, tempCardValida, credenciales } =
    useOneClickComercio();
  const { setOrder } = useOrder();
  const {
    calculateTotals,
    total,
    products,
    buyOrder,
    setBuyOrder,
    setFinishOrder,
    setPayStatus,
    descuento_id,
    descuento_data,
  } = useProductStore();
  const { machineSelected } = useMachine();
  const authorizePayOneClick = useAuthorizePayOneClick();
  const createOrder = useCreateOrder();
  const reverseTransaction = useReverseOneClickTransaction();
  const [loading, setLoading] = useState(true);
  const [pagando, setPagando] = useState(false);
  const totalFinalRef = useRef(0);
  const totalPedido = useRef(0);
  const confirmTransactionResp = useRef([]);
  const buyOrderIntento = useRef(null);
  const reversaLanzada = useRef(false);
  // Token de ownership del candado (ver paymentGuard.js): compartido entre
  // authorizePay/sendCreateTransaccion/sendPedido/reverseOneClickTransaction,
  // funciones separadas del componente que operan sobre el mismo intento.
  const candadoTokenRef = useRef(null);
  // Credenciales del intento que autorizó (borde 3): la reversa debe usar
  // SIEMPRE estas, nunca `machine` "fresco".
  const credencialesIntentoRef = useRef(null);
  // buyOrder AL MONTAR: es la clave del claim persistido (identifica la
  // llegada a esta ruta, sobrevive a un F5); el buyOrder ENVIADO a
  // Transbank es el regenerado por intento (buyOrderIntento.current).
  const buyOrderAlMontar = useRef(buyOrder);

  useEffect(() => {
    calculateTotals(machineSelected);

    authorizePay();
    // Este efecto debe ejecutarse una sola vez al montar, para autorizar el
    // pago OneClick con el estado inicial. Agregar las dependencias sugeridas
    // (authorizePay, calculateTotals, machineSelected) re-dispararía la
    // autorización en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authorizePay = ({ esReintento = false } = {}) => {
    // Validación de última instancia: este componente autoriza al montar,
    // sin interacción del usuario, así que es la única barrera real de esta
    // ruta contra un tempCard de otro comercio (422 garantizado en Transbank).
    // Se evalúa ANTES del candado/claim (AC 20): no debe consumirlos.
    if (!tempCardValida) {
      toast.error(MSG_TARJETA_OTRO_COMERCIO);
      setTempCard({});
      setLoading(false);
      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    // Leer el estado más reciente del store para evitar valores stale del closure
    const { total: storeTotal, discount: storeDiscount } =
      useProductStore.getState();
    const totalFinal = Math.max(0, storeTotal - storeDiscount);
    totalFinalRef.current = totalFinal;

    if (totalFinal <= 0) {
      setLoading(false);
      toast.error("El monto a pagar no puede ser $0");
      navigate(getCompanyRoute("cart"), { replace: true });
      return;
    }

    // Candado en vuelo (AC 7/8): compartido con el carrito ("oneclick"), un
    // solo pago en curso por pestaña.
    const candado = adquirirCandadoPago("oneclick");
    if (!candado) {
      setLoading(false);
      toast.error(PAYMENT_MSG_EN_CURSO);
      if (debeReportarBloqueo("oneclick")) {
        Sentry.captureMessage(
          "Segundo intento de pago bloqueado por candado",
          {
            level: "warning",
            tags: { payment_flow: "oneclick" },
            extra: {
              buyOrder: buyOrderIntento.current,
              motivo: "candado_en_curso",
              bloqueos: bloqueosAcumulados("oneclick"),
              tbk_user_len: String(tempCard?.tbk_user ?? "").length,
            },
          },
        );
      }
      return;
    }
    candadoTokenRef.current = candado;

    // Claim persistido (AC 7, edge 7): sólo se consulta en el disparo
    // automático de montaje, nunca en el reintento manual del botón "Pagar"
    // (esReintento=true) — el reintento explícito del usuario siempre debe
    // poder ejecutarse mientras el candado lo permita.
    if (
      !esReintento &&
      !reclamarIntento("oneclick:" + buyOrderAlMontar.current)
    ) {
      liberarCandadoPago("oneclick", candadoTokenRef.current);
      setLoading(false);
      toast.error(PAYMENT_MSG_EN_CURSO);
      if (debeReportarBloqueo("oneclick:claim")) {
        Sentry.captureMessage(
          "Segundo intento de pago bloqueado por candado",
          {
            level: "warning",
            tags: { payment_flow: "oneclick" },
            extra: {
              buyOrder: buyOrderAlMontar.current,
              motivo: "intento_ya_reclamado",
              bloqueos: bloqueosAcumulados("oneclick:claim"),
              tbk_user_len: String(tempCard?.tbk_user ?? "").length,
            },
          },
        );
      }
      return;
    }

    setPagando(true);
    reversaLanzada.current = false;

    // Se capturan las credenciales de este intento AHORA, antes del await
    // (misma razón que en CardOneClick.jsx: la reversa debe usarlas, no
    // `machine` "fresco").
    credencialesIntentoRef.current = credenciales;

    // buyOrder regenerado por intento (AC 9/11/12): usar SIEMPRE la variable
    // local `bo`, nunca el `buyOrder` del store (stale en este mismo tick).
    const bo = esReintento ? generarBuyOrderOneClick() : buyOrder;
    buyOrderIntento.current = bo;
    if (esReintento) setBuyOrder(bo);

    const body = {
      email: user.usuario_correo,
      buyOrder: bo,
      installmentsNumber: 1,
      total: totalFinal,
      token: tempCard.tbk_user,
      // §0.1: fix declarado apikey -> apiKey (el backend destructura apiKey).
      ...filtrarCredencialesParaBody({
        codeComerce: comercioEfectivo,
        codeStore: credenciales.codeStore,
        apiKey: credenciales.apiKey,
      }),
      maquina_id: machineSelected,
    };

    authorizePayOneClick
      .mutateAsync(body)
      .then((resp) => {
        if (resp?.status === "error" || !resp?.data?.details?.[0]) {
          // Segunda capa de ownership (ver paymentGuard.js): esta respuesta
          // puede ser la llegada tardía de un intento cuyo candado ya
          // expiró por TTL y fue re-adquirido por un reintento más nuevo. Si
          // ya no es el dueño vigente, se descarta silenciosamente en vez de
          // dejar la pantalla "No se pudo realizar el pago" por un rechazo
          // que no le corresponde a la compra que ya completó el intento
          // nuevo.
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          // Rechazo real del backend/Transbank (ej. 422 de comercio o
          // username). Sin financial_risk: ocurre antes de autorizar, no hay
          // cobro. Nunca se envía el tbk_user completo, sólo su longitud.
          Sentry.captureMessage(
            "Rechazo de authorizePayOneClick (pay-detail OneClick)",
            {
              level: "error",
              tags: { payment_flow: "oneclick" },
              extra: {
                buyOrder: bo,
                maquina_id: machineSelected,
                codigo_comercio: comercioEfectivo,
                tbk_user_len: String(tempCard?.tbk_user ?? "").length,
                backendMessage: resp?.message,
              },
            },
          );
          setPayStatus(false);
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setLoading(false);
          toast.error("Error al realizar el pago");
          return;
        }

        const data = resp.data;
        if (data.details[0].status == "AUTHORIZED") {
          // Segunda capa de ownership (ver paymentGuard.js): esta respuesta
          // puede ser la llegada tardía de un intento cuyo candado ya
          // expiró por TTL y fue re-adquirido por un reintento más nuevo. Si
          // ya no es el dueño vigente, se descarta silenciosamente esta
          // rama en vez de seguir a sendCreateTransaccion/sendPedido/
          // createOrder con datos obsoletos (evita un doble pedido/cobro).
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          setPayStatus(true);
          sendCreateTransaccion(data);
        } else {
          // Misma segunda capa de ownership que la rama de éxito: un rechazo
          // tardío de un intento obsoleto (candado ya re-adquirido por un
          // intento más nuevo) no debe pintar "No se pudo realizar el pago".
          if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
            Sentry.captureMessage(
              "Respuesta tardía de un intento de pago obsoleto descartada",
              {
                level: "warning",
                tags: { payment_flow: "oneclick" },
                extra: {
                  buyOrder: bo,
                  maquina_id: machineSelected,
                  motivo: "token_candado_ya_no_vigente",
                },
              },
            );
            return;
          }
          setPayStatus(false);
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setLoading(false);
          toast.error("El pago fue rechazado por el banco");
        }
      })
      .catch((error) => {
        // Misma segunda capa de ownership: el error de red de un intento
        // obsoleto (candado ya re-adquirido por un intento más nuevo) no
        // debe pintar "No se pudo realizar el pago".
        if (!esTokenVigente("oneclick", candadoTokenRef.current)) {
          Sentry.captureMessage(
            "Respuesta tardía de un intento de pago obsoleto descartada",
            {
              level: "warning",
              tags: { payment_flow: "oneclick" },
              extra: {
                buyOrder: bo,
                maquina_id: machineSelected,
                motivo: "token_candado_ya_no_vigente",
              },
            },
          );
          return;
        }
        Sentry.captureException(error, {
          tags: { payment_flow: "oneclick" },
          extra: {
            buyOrder: bo,
            maquina_id: machineSelected,
            codigo_comercio: comercioEfectivo,
            tbk_user_len: String(tempCard?.tbk_user ?? "").length,
          },
        });
        setPayStatus(false);
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        setLoading(false);
        toast.error("Error al realizar el pago");
      });
  };

  const sendCreateTransaccion = (data) => {
    // Incidencia #10: se eliminó la llamada directa a createTransaccion porque
    // createOrder ya registra la transacción en el backend evitando duplicados.
    confirmTransactionResp.current = {
      amount: data.details[0].amount,
      status: data.details[0].status,
      buy_order: data.buy_order,
      card_number: data.card_detail.card_number,
      accounting_date: data.accounting_date,
      transaction_date: data.transaction_date,
      authorization_code: data.details[0].authorization_code,
      payment_type_code: data.details[0].payment_type_code,
      response_code: data.details[0].response_code,
      installments_number: data.details[0].installments_number,
      commerce_code: data.details[0].commerce_code,
    };
    totalPedido.current = data.details[0].amount;
    sendPedido();
  };

  const reverseOneClickTransaction = () => {
    // AC 10/edge 5: la reversa se ejecuta una sola vez por autorización.
    if (reversaLanzada.current) return;
    reversaLanzada.current = true;

    setPayStatus(false);

    // Borde 3/AC-11: credenciales del intento que autorizó, nunca `machine`
    // "fresco"; si el ref está vacío, cae a la resolución en vivo y nunca
    // aborta la reversa (P2).
    let cred = credencialesIntentoRef.current;
    if (!cred) {
      cred = credenciales;
      Sentry.captureMessage(
        "Reversa OneClick sin credenciales de intento capturadas, usando resolución en vivo",
        {
          level: "warning",
          tags: { payment_flow: "oneclick" },
          extra: { buyOrder: buyOrderIntento.current, maquina_id: machineSelected },
        },
      );
    }

    let formData = {
      buyOrder: buyOrderIntento.current,
      codigoTienda: cred.codeStore,
      total: totalFinalRef.current,
      maquina_id: machineSelected,
      ...filtrarCredencialesParaBody({
        codeComerce: cred.codeComerce,
        codeStore: cred.codeStore,
        apiKey: cred.apiKey,
      }),
    };
    reverseTransaction
      .mutateAsync(formData)
      .then(() => {
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        return navigate(getCompanyRoute("cart"), { replace: true });
      })
      .catch((error) => {
        // Riesgo financiero: el cobro pudo haber quedado hecho y la reversa
        // (el intento de anularlo) falló — necesita revisión manual.
        Sentry.captureException(error, {
          level: "error",
          tags: { financial_risk: true, payment_flow: "oneclick" },
          extra: { buyOrder: buyOrderIntento.current, maquina_id: machineSelected },
        });
        liberarCandadoPago("oneclick", candadoTokenRef.current);
        setPagando(false);
        setLoading(false);
        toast.error("Error al reversar la transacción");
        return navigate(getCompanyRoute("cart"), { replace: true });
      });
  };

  const sendPedido = () => {
    let dataSend = {
      appPlatform: "web",
      userId: user.usuario_id,
      cart: mapCartForOrder(products, machineSelected),
      storeId: machineSelected,
      total: totalPedido.current,
      buyOrder: buyOrderIntento.current,
      appliedDiscount: buildAppliedDiscount(descuento_data, descuento_id),

      transactionDetail: {
        // Incidencia #1: agregar buy_order al nivel superior de transactionDetail
        // para que el backend lo guarde correctamente en la tabla transacciones.
        buy_order: buyOrderIntento.current,
        details: [
          {
            amount: confirmTransactionResp.current.amount,
            status: confirmTransactionResp.current.status,
            authorization_code:
              confirmTransactionResp.current.authorization_code,
            payment_type_code: confirmTransactionResp.current.payment_type_code,
            response_code: confirmTransactionResp.current.response_code,
            installments_number:
              confirmTransactionResp.current.installments_number,
            commerce_code: confirmTransactionResp.current.commerce_code,
            buy_order: buyOrderIntento.current,
            prepaid_balance: 0,
          },
        ],
        card_detail: {
          card_number: confirmTransactionResp.current.card_number,
        },
        accounting_date: confirmTransactionResp.current.accounting_date,
        transaction_date: confirmTransactionResp.current.transaction_date,
      },
    };
    // console.log("createOrder", dataSend);
    createOrder
      .mutateAsync(dataSend)
      .then((resp) => {
        //MANEJAR ERROR
        if (resp.status === "success") {
          // Camino de éxito (AC 6): el candado NO se libera; la navegación a
          // /order es el único desenlace posible desde aquí.
          setOrder({ ...resp, pedido_total: totalPedido.current });
          setFinishOrder(machineSelected);
          toast.success("EXITO");
          incrementTotalPedidos();
          return navigate(getCompanyRoute("order"), { replace: true });
        } else {
          liberarCandadoPago("oneclick", candadoTokenRef.current);
          setPagando(false);
          setLoading(false);
          toast.error(resp.message);
          //return navigate(`/cart`, {replace: true});
        }
      })
      .catch(() => {
        toast.error("Error al realizar el pedido");
        reverseOneClickTransaction();
        //MANEJAR ERROR
      });
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12 mb-3 inter-regular text-center">
          <h3 className="text-black inter-semibold ">
            Validando la compra, por favor espere!
          </h3>
          <Spin tip="Verificando..." size="large">
            <div
              style={{
                padding: 50,
                borderRadius: 4,
              }}
            />
          </Spin>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12 mb-3 inter-regular text-center">
        <h3 className="text-black inter-semibold ">
          No se pudo realizar el pago!
        </h3>
        <div className="mt-4">
          <Link to={getCompanyRoute(`store/${machineSelected}`)}>
            <Button
              className="inter-semibold mx-1"
              variant="secondary"
              size="sm"
            >
              Seguir comprando
            </Button>
          </Link>
          <Button
            type="submit"
            className="btn verde text-white inter-semibold"
            size="sm"
            disabled={pagando}
            onClick={() => authorizePay({ esReintento: true })}
          >
            {pagando ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  role="status"
                  aria-hidden="true"
                ></Spinner>
                {PAYMENT_MSG_EN_CURSO}
              </>
            ) : (
              `Pagar ${formatMoney(total)}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
